import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import differenceWith from 'lodash/differenceWith';
import intersectionWith from 'lodash/intersectionWith';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import findIndex from 'lodash/findIndex';
import { Filter } from './filter_components';
import { FloatingWrapper } from './styled_components';
import HalfWidthFilterContainer from './filter_components/styled/HalfWidthFilterContainer';
import SizesFilterWrapper from './filter_components/styled/SizesFilterWrapper';
import CountrySelector from './filter_components/CountrySelector';
import VerticalSpacer from '../VerticalSpacer/VerticalSpacer';
import filterData from '../../common/models/SizeFilterData';
import { SizeData } from '../../common/models/SizeData';
import { ShoeSizeData } from '../../common/models/ShoeSizeData';

import Departments from '../../common/models/Departments';
import Country from '../../common/models/Country';
import { getCookieValue, setCookieValue } from '../../helpers';

const defaultCountryForKids = 'eu';

class SizesFilter extends Component {
  constructor(props) {
    super(props);

    const { department } = props;

    this.updateSelection = this.updateSelection.bind(this);
    this.renderSizeFilter = this.renderSizeFilter.bind(this);

    if (this.isCorrectCountryId(props.countryId)) {
      this.state = { selectedCountryId: props.countryId };
    } else {
      const selectedCountryId = Departments.getKidsDepartmentsInternalNames().includes(department)
        ? defaultCountryForKids
        : Country.italy();
      this.state = { selectedCountryId };
    }
  }

  componentDidMount() {
    const previousCountryId = getCookieValue('shoeSizeCountryId');
    if (this.isCorrectCountryId(previousCountryId)) {
      this.setState({ selectedCountryId: previousCountryId });
    }
  }

  isCorrectCountryId(id) {
    const { department, s3BasePath } = this.props;
    const country = filterData(s3BasePath).find(el => el.id === id);
    const availableISOCodes = ShoeSizeData.countriesISO(department);
    return country && availableISOCodes.indexOf(country.iso) !== -1;
  }

  // changing selection will update the filters to selected country size
  updateSelection(selectedSizes) {
    const { currSizes: alreadySelectedSizes } = this.props;
    const countryId = this.state.selectedCountryId;
    const { handleFilterClick } = this.props;
    let selectedSizesInSelectedCountry = this.convertedSizesToSelectedCountry(alreadySelectedSizes, countryId);
    const data = selectedSizes;
    const selected = intersectionWith(selectedSizesInSelectedCountry, [selectedSizes], isEqual);

    if (selected.length !== 0) {
      selectedSizesInSelectedCountry = differenceWith(selectedSizesInSelectedCountry, [data], isEqual);
    } else {
      selectedSizesInSelectedCountry = selectedSizesInSelectedCountry.concat(selectedSizes);
    }
    handleFilterClick(selectedSizesInSelectedCountry.map(el => el.toStringRepresentation()));
  }

  renderSizeFilter(sizeData, index) {
    const { currSizes, sizes, isDesktop } = this.props;
    const sizeDataString = sizeData.toStringRepresentation();
    const countryId = this.state.selectedCountryId;

    const sizesInSelectedCountry = this.convertedSizesToSelectedCountry(currSizes, countryId);

    const available = findIndex(sizes, el => sizeDataString === el.toStringRepresentation()) > -1;
    const selected = findIndex(sizesInSelectedCountry, el => sizeDataString === el.toStringRepresentation()) > -1;

    return (
      <HalfWidthFilterContainer key={`size_filter_${index}`}>
        <Filter
          handleFilterClick={() => {
            this.updateSelection(sizeData.data);
          }}
          isDesktop={isDesktop}
          id={sizeData.toDisplayString()}
          checked={selected}
          active={available}
          value={sizeData.toDisplayString()}
          title={sizeData.toDisplayString() === 'U' ? 'ONE SIZE' : sizeData.toDisplayString()}
          type="size"
          filterName="sizeFilterSelect"
        />
      </HalfWidthFilterContainer>
    );
  }

  convertedSizesToSelectedCountry(sizes, countryId) {
    const { s3BasePath } = this.props;
    if (!sizes.some(el => el.isShoeSize())) {
      return sizes;
    }

    const selectedCountry = filterData(s3BasePath).filter(el => el.id === countryId)[0].iso;
    return sizes
      .map(size => {
        const equal = ShoeSizeData.allEqual(size.data);
        return equal.filter(shoeSize => shoeSize.countryISO === selectedCountry)[0];
      })
      .filter(el => el);
  }

  reorderedSizesFor2ColumnDisplay(sizes) {
    const rows = Math.ceil(sizes.length / 2);
    const arr = sizes.slice();

    sizes.forEach((el, idx) => {
      const offset = Math.floor(idx / 2);
      arr[idx] = sizes[idx % 2 === 0 ? offset : offset + rows];
    });

    return arr;
  }

  countrySelectionChanged = newId => {
    setCookieValue('shoeSizeCountryId', newId, { path: '/' });
    this.setState({ selectedCountryId: newId }, () => this.updateSelection([]));
  };

  renderCountrySelector = department => {
    const { s3BasePath } = this.props;
    const codes = ShoeSizeData.countriesISO(department);
    const filteredData = intersectionWith(filterData(s3BasePath), codes, (arrVal, otherVal) => arrVal.iso === otherVal);

    return (
      <Fragment>
        <CountrySelector
          countries={filteredData}
          selectedCountryId={this.state.selectedCountryId}
          handleSelection={this.countrySelectionChanged}
        />
        <VerticalSpacer height={'22px'} style={{ width: '100%' }} />
      </Fragment>
    );
  };

  renderClear = () => (
    <FloatingWrapper isDesktop={this.props.isDesktop}>
      <a style={{ color: '#000' }} onClick={() => this.props.handleFilterClick([])}>
        clear
      </a>
    </FloatingWrapper>
  );

  render() {
    const { department, currSizes, isShoesCategory, s3BasePath } = this.props;
    const { selectedCountryId } = this.state;
    const multipleSelected = currSizes.length > 0;

    let sizes = SizeData.crossCategorySizes(department);

    if (isShoesCategory) {
      const selectedCountryISO = filterData(s3BasePath).filter(el => el.id === selectedCountryId)[0].iso;
      sizes = SizeData.shoeSizes(selectedCountryISO, department);
    }

    return (
      <SizesFilterWrapper>
        {isShoesCategory && this.renderCountrySelector(department)}
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {multipleSelected && this.renderClear()}
          {map(this.reorderedSizesFor2ColumnDisplay(sizes), (curr, index) => this.renderSizeFilter(curr, index))}
        </div>
      </SizesFilterWrapper>
    );
  }
}

SizesFilter.defaultProps = {
  isShoesCategory: false
};

SizesFilter.propTypes = {
  countryId: PropTypes.string.isRequired,
  currSizes: PropTypes.arrayOf(PropTypes.object),
  department: PropTypes.string.isRequired,
  handleFilterClick: PropTypes.func,
  isDesktop: PropTypes.bool,
  isShoesCategory: PropTypes.bool,
  isWomenCategory: PropTypes.bool,
  s3BasePath: PropTypes.string.isRequired,
  sizes: PropTypes.arrayOf(PropTypes.object)
};

export default SizesFilter;
